import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import PageHeader from '../components/PageHeader';
import DataState from '../components/DataState';
import StatCard from '../components/StatCard';
import {
  fetchWebsiteStores,
  updateStoreVisibility,
} from '../api/websiteStores';
import { colors } from '../design-system/tokens';

/**
 * 매장 공개 노출 관리 (master 전용 — websiteStores.manage)
 *
 * 테스트/QA 매장은 isActive를 유지해 콘솔 검증은 가능하게 두고, 고객 공개 표면만 끈다.
 * - isTestStore: 앱/웹 공개 노출을 강제로 막는 QA 매장 마커.
 * - showInAppStorePicker: 고객 앱 store picker 노출 여부.
 * - showOnWebsite: mogoo.co.kr 공개 웹 노출 여부.
 * - GET /stores/website-manage 로 목록, PATCH /stores/:id 로 토글.
 * - 실제 공개 노출은 isActive && !isTestStore && 각 노출 플래그를 모두 통과해야 한다.
 */

const CATEGORY_LABELS = {
  carwash: '세차',
  academy: '학원',
  gym: '헬스장',
  laundry: '세탁소',
  cafe: '카페',
  electronics: '전자제품',
  pet: '반려동물',
};

const hasText = (value) => !!String(value ?? '').trim();
const hasHours = (hours) => !!(hours?.open && hours?.close);
const isCarwashWebsiteStore = (row) => !row.category || row.category === 'carwash';
const hasWebsiteRequiredInfo = (row) =>
  hasText(row.address) && (hasHours(row.hours) || hasText(row.phone));
const isVisibleInApp = (row) =>
  !!row.isActive &&
  !row.isTestStore &&
  row.showInAppStorePicker !== false;
const isVisibleOnHome = (row) =>
  !!row.showOnWebsite &&
  !!row.isActive &&
  !row.isTestStore &&
  isCarwashWebsiteStore(row) &&
  hasWebsiteRequiredInfo(row);

const appExclusionLabel = (row) => {
  if (!row.showInAppStorePicker || isVisibleInApp(row)) return '';
  if (!row.isActive) return '비활성이라 미노출';
  if (row.isTestStore) return '테스트 매장이라 미노출';
  return '';
};

const websiteExclusionLabel = (row) => {
  if (!row.showOnWebsite || isVisibleOnHome(row)) return '';
  if (!row.isActive) return '비활성이라 미노출';
  if (row.isTestStore) return '테스트 매장이라 미노출';
  if (!isCarwashWebsiteStore(row)) return '세차장 섹션 제외';
  if (!hasWebsiteRequiredInfo(row)) return '정보 미완성이라 미노출';
  return '';
};

const WebsiteStores = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [rowError, setRowError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchWebsiteStores();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || '매장 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleCount = useMemo(
    () => rows.filter(isVisibleOnHome).length,
    [rows],
  );
  const appVisibleCount = useMemo(
    () => rows.filter(isVisibleInApp).length,
    [rows],
  );
  const testStoreCount = useMemo(
    () => rows.filter((row) => row.isTestStore).length,
    [rows],
  );

  const updateRow = async (row, patch) => {
    setSavingId(row._id);
    setRowError('');
    setRows((prev) =>
      prev.map((r) => (r._id === row._id ? { ...r, ...patch } : r)),
    );
    try {
      await updateStoreVisibility(row._id, patch);
    } catch (e) {
      setRows((prev) =>
        prev.map((r) =>
          r._id === row._id ? row : r,
        ),
      );
      setRowError(
        e?.response?.data?.message || '노출 설정 변경에 실패했습니다.',
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleTestToggle = (row) => {
    const next = !row.isTestStore;
    updateRow(row, {
      isTestStore: next,
      ...(next
        ? {
            showInAppStorePicker: false,
            showOnWebsite: false,
          }
        : {}),
    });
  };

  return (
    <Box>
      <PageHeader
        title="매장 공개 노출"
        description="테스트 매장은 점주 콘솔 QA에는 남기고, 고객 앱과 mogoo.co.kr 공개 화면에서는 숨깁니다."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label="앱 선택 노출"
          value={`${appVisibleCount} / ${rows.length}`}
          caption="앱 store picker 공개"
        />
        <StatCard
          label="웹 노출 중"
          value={`${visibleCount} / ${rows.length}`}
          caption="홈 필터 통과 + 웹 노출 ON"
        />
        <StatCard
          label="테스트 매장"
          value={`${testStoreCount}`}
          caption="QA 전용, 고객 화면 미노출"
        />
      </Stack>

      {rowError && (
        <Paper
          sx={{ p: 2, mb: 2, bgcolor: colors.status.danger.bg }}
          role="alert"
        >
          <Typography variant="body2" sx={{ color: colors.status.danger.fg }}>
            {rowError}
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={load}>
          새로고침
        </Button>
      </Box>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="관리할 매장이 없습니다."
        skeleton={{ rows: 5, cols: 4 }}
      >
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>매장</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>카테고리</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>운영 상태</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    테스트 매장
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    앱 선택 노출
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    웹 노출
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
                      <Typography variant="caption" sx={{ color: colors.text.muted }}>
                        {row.code}
                        {row.address ? ` · ${row.address}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {CATEGORY_LABELS[row.category] || row.category}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.isActive ? (
                        <Chip
                          size="small"
                          label="운영 중"
                          sx={{
                            bgcolor: colors.status.success.bg,
                            color: colors.status.success.fg,
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="비활성"
                          sx={{
                            bgcolor: colors.status.neutral.bg,
                            color: colors.status.neutral.fg,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Switch
                          checked={!!row.isTestStore}
                          disabled={savingId === row._id}
                          onChange={() => handleTestToggle(row)}
                          slotProps={{
                            input: { 'aria-label': `${row.name} 테스트 매장` },
                          }}
                        />
                        {row.isTestStore && (
                          <Chip
                            size="small"
                            label="QA 전용"
                            sx={{
                              bgcolor: colors.status.info.bg,
                              color: colors.status.info.fg,
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Switch
                          checked={!row.isTestStore && row.showInAppStorePicker !== false}
                          disabled={savingId === row._id || !!row.isTestStore}
                          onChange={() =>
                            updateRow(row, {
                              showInAppStorePicker: row.showInAppStorePicker === false,
                            })
                          }
                          slotProps={{
                            input: { 'aria-label': `${row.name} 앱 선택 노출` },
                          }}
                        />
                        {appExclusionLabel(row) && (
                          <Typography
                            variant="caption"
                            sx={{ color: colors.status.warning.fg }}
                          >
                            {appExclusionLabel(row)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Switch
                          checked={!row.isTestStore && !!row.showOnWebsite}
                          disabled={savingId === row._id || !!row.isTestStore}
                          onChange={() =>
                            updateRow(row, { showOnWebsite: !row.showOnWebsite })
                          }
                          slotProps={{
                            input: { 'aria-label': `${row.name} 웹 노출` },
                          }}
                        />
                        {websiteExclusionLabel(row) && (
                          <Typography
                            variant="caption"
                            sx={{ color: colors.status.warning.fg }}
                          >
                            {websiteExclusionLabel(row)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 2, color: colors.text.muted, alignItems: 'center' }}
      >
        <PublicRoundedIcon fontSize="small" />
        <Typography variant="caption">
          테스트 매장을 켜면 앱 선택 노출과 웹 노출이 자동으로 꺼집니다. 다시 공개하려면 테스트 매장을 먼저 해제하세요.
        </Typography>
      </Stack>
    </Box>
  );
};

export default WebsiteStores;
